import {
    ApplicationAccessData,
    ApplicationId,
    ApplicationIdentityDefinition,
    ApplicationTrustedDomainValue,
    ApplicationThemeDefinition,
} from 'yeself-common/typings/application/Application';

export const REQUEST_STATUS = {
    IDLE: 'idle',
    PENDING: 'pending',
    SUCCESS: 'success',
    FAILED: 'failed',
} as const;

export const UPDATE_APPLICATION_ACCESS_REQUEST = 'settings/UPDATE_APPLICATION_ACCESS_REQUEST';
export const UPDATE_APPLICATION_ACCESS_SUCCESS = 'settings/UPDATE_APPLICATION_ACCESS_SUCCESS';
export const UPDATE_APPLICATION_ACCESS_FAIL = 'settings/UPDATE_APPLICATION_ACCESS_FAIL';
export const UPDATE_APPLICATION_ACCESS_IDLE = 'settings/UPDATE_APPLICATION_ACCESS_IDLE';

export const UPDATE_APPLICATION_IDENTITY_REQUEST = 'settings/UPDATE_APPLICATION_IDENTITY_REQUEST';
export const UPDATE_APPLICATION_IDENTITY_SUCCESS = 'settings/UPDATE_APPLICATION_IDENTITY_SUCCESS';
export const UPDATE_APPLICATION_IDENTITY_FAIL = 'settings/UPDATE_APPLICATION_IDENTITY_FAIL';
export const UPDATE_APPLICATION_IDENTITY_IDLE = 'settings/UPDATE_APPLICATION_IDENTITY_IDLE';

export const UPDATE_APPLICATION_EXTENSION_URLS_REQUEST =
    'settings/UPDATE_APPLICATION_EXTENSION_URLS_REQUEST';
export const UPDATE_APPLICATION_EXTENSION_URLS_SUCCESS =
    'settings/UPDATE_APPLICATION_EXTENSION_URLS_SUCCESS';
export const UPDATE_APPLICATION_EXTENSION_URLS_FAIL = 'settings/UPDATE_APPLICATION_EXTENSION_URLS_FAIL';

export const UPDATE_APPLICATION_THEME_REQUEST = 'settings/UPDATE_APPLICATION_THEME_REQUEST';
export const UPDATE_APPLICATION_THEME_SUCCESS = 'settings/UPDATE_APPLICATION_THEME_SUCCESS';
export const UPDATE_APPLICATION_THEME_FAIL = 'settings/UPDATE_APPLICATION_THEME_FAIL';
export const UPDATE_APPLICATION_THEME_IDLE = 'settings/UPDATE_APPLICATION_THEME_IDLE';

export const UPDATE_APPLICATION_TRUSTED_DOMAINS_REQUEST =
    'settings/UPDATE_APPLICATION_TRUSTED_DOMAINS_REQUEST';
export const UPDATE_APPLICATION_TRUSTED_DOMAINS_SUCCESS =
    'settings/UPDATE_APPLICATION_TRUSTED_DOMAINS_SUCCESS';
export const UPDATE_APPLICATION_TRUSTED_DOMAINS_FAIL =
    'settings/UPDATE_APPLICATION_TRUSTED_DOMAINS_FAIL';
export const UPDATE_APPLICATION_TRUSTED_DOMAINS_IDLE =
    'settings/UPDATE_APPLICATION_TRUSTED_DOMAINS_IDLE';

export const FETCH_INSTALLED_LIST_ADDONS_REQUEST = 'settings/FETCH_INSTALLED_LIST_ADDONS_REQUEST';
export const FETCH_INSTALLED_LIST_ADDONS_SUCCESS = 'settings/FETCH_INSTALLED_LIST_ADDONS_SUCCESS';
export const FETCH_INSTALLED_LIST_ADDONS_FAIL = 'settings/FETCH_INSTALLED_LIST_ADDONS_FAIL';
export const FETCH_INSTALLED_LIST_ADDONS_IDLE = 'settings/FETCH_INSTALLED_LIST_ADDONS_IDLE';

export const FETCH_AVAILABLE_LIST_ADDONS_REQUEST = 'settings/FETCH_AVAILABLE_LIST_ADDONS_REQUEST';
export const FETCH_AVAILABLE_LIST_ADDONS_SUCCESS = 'settings/FETCH_AVAILABLE_LIST_ADDONS_SUCCESS';
export const FETCH_AVAILABLE_LIST_ADDONS_FAIL = 'settings/FETCH_AVAILABLE_LIST_ADDONS_FAIL';
export const FETCH_AVAILABLE_LIST_ADDONS_IDLE = 'settings/FETCH_AVAILABLE_LIST_ADDONS_IDLE';

export const ROLLOUT_POLICIES_BY_MY_ORGANIZATIONS_REQUEST = `settings/ROLLOUT_POLICIES_BY_MY_ORGANIZATIONS_REQUEST`;
export const ROLLOUT_POLICIES_BY_MY_ORGANIZATIONS_SUCCESS = `settings/ROLLOUT_POLICIES_BY_MY_ORGANIZATIONS_SUCCESS`;
export const ROLLOUT_POLICIES_BY_MY_ORGANIZATIONS_ERROR = `settings/ROLLOUT_POLICIES_BY_MY_ORGANIZATIONS_ERROR`;

export const ROLLOUT_POLICIES_BY_SPACE_ID_REQUEST = `settings/ROLLOUT_POLICIES_BY_SPACE_ID_REQUEST`;
export const ROLLOUT_POLICIES_BY_SPACE_ID_SUCCESS = `settings/ROLLOUT_POLICIES_BY_SPACE_ID_SUCCESS`;
export const ROLLOUT_POLICIES_BY_SPACE_ID_ERROR = `settings/ROLLOUT_POLICIES_BY_SPACE_ID_ERROR`;

export type SettingsState = {
    hasError: boolean;
    isLoading: boolean;
    updateStatus: typeof REQUEST_STATUS[keyof typeof REQUEST_STATUS];
    isContentLanguageInitialized: boolean;
    data: Record<string, unknown>;
    languageList: {
        hasError: boolean;
        isLoading: boolean;
        data: Record<string, unknown>[];
    };
    usersAccess: {
        isLoading: boolean;
        hasError: boolean;
        data: Record<string, unknown>[];
    };
    addonsAvailable: {
        fetchStatus: typeof REQUEST_STATUS[keyof typeof REQUEST_STATUS];
        reason: Record<string, unknown> | null;
        data: Record<string, unknown>[];
    };
    addonsInstalled: {
        fetchStatus: typeof REQUEST_STATUS[keyof typeof REQUEST_STATUS];
        reason: Record<string, unknown> | null;
        data: Record<string, unknown>[];
    };
    addons: {
        selected: unknown;
    };
    addonsScripts: Record<string, unknown>;
    rolloutPolicies: {
        availableRolloutPolicies: {
            fetchStatus: typeof REQUEST_STATUS[keyof typeof REQUEST_STATUS];
            reason: Record<string, unknown> | null;
            data: AvailableRolloutPolicy[];
        };
        rolloutPoliciesBySpace: {
            fetchStatus: typeof REQUEST_STATUS[keyof typeof REQUEST_STATUS];
            reason: Record<string, unknown> | null;
            data: RolloutPolicyWithUrl[];
        };
        extensionUrls: ExtensionUrl[];
    };
};

export type ExtensionUrl = {
    rolloutPolicyId: string | undefined;
    url: string;
};

export type RolloutPolicyWithUrl = {
    id: string;
    urlPattern: string;
};

export type AvailableRolloutPolicy = {
    id: string;
    name: string;
    organizationId: string;
};

type UpdateApplicationAccessRequestAction = {
    type: typeof UPDATE_APPLICATION_ACCESS_REQUEST;
    payload: { applicationId: ApplicationId; access: ApplicationAccessData };
};

type UpdateApplicationAccessSuccessAction = {
    type: typeof UPDATE_APPLICATION_ACCESS_SUCCESS;
    payload: { applicationId: ApplicationId; access: ApplicationAccessData };
};

type UpdateApplicationAccessFailAction = {
    type: typeof UPDATE_APPLICATION_ACCESS_FAIL;
};

type UpdateApplicationAccessIdleAction = {
    type: typeof UPDATE_APPLICATION_ACCESS_IDLE;
};

type UpdateApplicationIdentityRequestAction = {
    type: typeof UPDATE_APPLICATION_IDENTITY_REQUEST;
    payload: { applicationId: ApplicationId; identity: ApplicationIdentityDefinition };
};

type UpdateApplicationIdentitySuccessAction = {
    type: typeof UPDATE_APPLICATION_IDENTITY_SUCCESS;
    payload: { applicationId: ApplicationId; identity: ApplicationIdentityDefinition };
};

type UpdateApplicationIdentityFailAction = {
    type: typeof UPDATE_APPLICATION_IDENTITY_FAIL;
};

type UpdateApplicationIdentityIdleAction = {
    type: typeof UPDATE_APPLICATION_IDENTITY_IDLE;
};

type UpdateApplicationThemeRequestAction = {
    type: typeof UPDATE_APPLICATION_THEME_REQUEST;
    payload: { applicationId: ApplicationId; theme: ApplicationThemeDefinition };
};

type UpdateApplicationThemeSuccessAction = {
    type: typeof UPDATE_APPLICATION_THEME_SUCCESS;
    payload: { applicationId: ApplicationId; theme: ApplicationThemeDefinition };
};

type UpdateApplicationThemeFailAction = {
    type: typeof UPDATE_APPLICATION_THEME_FAIL;
};

type UpdateApplicationThemeIdleAction = {
    type: typeof UPDATE_APPLICATION_THEME_IDLE;
};

type UpdateApplicationOriginUrlsRequestAction = {
    type: typeof UPDATE_APPLICATION_EXTENSION_URLS_REQUEST;
    payload: { applicationId: ApplicationId; extensionUrls: ExtensionUrl[] };
};

type UpdateApplicationOriginUrlsSuccessAction = {
    type: typeof UPDATE_APPLICATION_EXTENSION_URLS_SUCCESS;
    payload: { applicationId: ApplicationId; extensionUrls: ExtensionUrl[] };
};

type UpdateApplicationOriginUrlsFailAction = {
    type: typeof UPDATE_APPLICATION_EXTENSION_URLS_FAIL;
};

type UpdateApplicationTrustedDomainsRequestAction = {
    type: typeof UPDATE_APPLICATION_TRUSTED_DOMAINS_REQUEST;
    payload: { applicationId: ApplicationId; trustedDomains: ApplicationTrustedDomainValue[] };
};

type UpdateApplicationTrustedDomainsSuccessAction = {
    type: typeof UPDATE_APPLICATION_TRUSTED_DOMAINS_SUCCESS;
    payload: { applicationId: ApplicationId; trustedDomains: ApplicationTrustedDomainValue[] };
};

type UpdateApplicationTrustedDomainsFailAction = {
    type: typeof UPDATE_APPLICATION_TRUSTED_DOMAINS_FAIL;
};

type UpdateApplicationTrustedDomainsIdleAction = {
    type: typeof UPDATE_APPLICATION_TRUSTED_DOMAINS_IDLE;
};

type FetchInstalledListAddonsRequestAction = {
    type: typeof FETCH_INSTALLED_LIST_ADDONS_REQUEST;
    payload: ApplicationId;
};

type FetchInstalledListAddonsSuccessAction = {
    type: typeof FETCH_INSTALLED_LIST_ADDONS_SUCCESS;
    payload: Record<string, unknown>[];
};

type FetchInstalledListAddonsFailAction = {
    type: typeof FETCH_INSTALLED_LIST_ADDONS_FAIL;
    payload: Record<string, unknown>;
};

type FetchInstalledListAddonsIdleAction = {
    type: typeof FETCH_INSTALLED_LIST_ADDONS_IDLE;
};

type FetchAvailableListAddonsRequestAction = {
    type: typeof FETCH_AVAILABLE_LIST_ADDONS_REQUEST;
    payload: ApplicationId;
};

type FetchAvailableListAddonsSuccessAction = {
    type: typeof FETCH_AVAILABLE_LIST_ADDONS_SUCCESS;
    payload: Record<string, unknown>[];
};

type FetchAvailableListAddonsFailAction = {
    type: typeof FETCH_AVAILABLE_LIST_ADDONS_FAIL;
    payload: Record<string, unknown>;
};

type FetchAvailableListAddonsIdleAction = {
    type: typeof FETCH_AVAILABLE_LIST_ADDONS_IDLE;
};

type RolloutPoliciesByMyOrganizationsRequestAction = {
    type: typeof ROLLOUT_POLICIES_BY_MY_ORGANIZATIONS_REQUEST;
};

type RolloutPoliciesByMyOrganizationsSuccessAction = {
    type: typeof ROLLOUT_POLICIES_BY_MY_ORGANIZATIONS_SUCCESS;
    payload: AvailableRolloutPolicy[];
};

type RolloutPoliciesByMyOrganizationsErrorAction = {
    type: typeof ROLLOUT_POLICIES_BY_MY_ORGANIZATIONS_ERROR;
    payload: Record<string, unknown>;
};

type RolloutPoliciesBySpaceIdRequestAction = {
    type: typeof ROLLOUT_POLICIES_BY_SPACE_ID_REQUEST;
};

type RolloutPoliciesBySpaceIdSuccessAction = {
    type: typeof ROLLOUT_POLICIES_BY_SPACE_ID_SUCCESS;
    payload: RolloutPolicyWithUrl[];
};

type RolloutPoliciesBySpaceIdErrorAction = {
    type: typeof ROLLOUT_POLICIES_BY_SPACE_ID_ERROR;
    payload: Record<string, unknown>;
};

export type SettingsActionTypes =
    | UpdateApplicationAccessRequestAction
    | UpdateApplicationAccessSuccessAction
    | UpdateApplicationAccessFailAction
    | UpdateApplicationAccessIdleAction
    | UpdateApplicationIdentityRequestAction
    | UpdateApplicationIdentitySuccessAction
    | UpdateApplicationIdentityFailAction
    | UpdateApplicationIdentityIdleAction
    | UpdateApplicationThemeRequestAction
    | UpdateApplicationThemeSuccessAction
    | UpdateApplicationThemeFailAction
    | UpdateApplicationThemeIdleAction
    | UpdateApplicationOriginUrlsRequestAction
    | UpdateApplicationOriginUrlsFailAction
    | UpdateApplicationOriginUrlsSuccessAction
    | UpdateApplicationTrustedDomainsRequestAction
    | UpdateApplicationTrustedDomainsFailAction
    | UpdateApplicationTrustedDomainsSuccessAction
    | UpdateApplicationTrustedDomainsIdleAction
    | FetchInstalledListAddonsRequestAction
    | FetchInstalledListAddonsSuccessAction
    | FetchInstalledListAddonsFailAction
    | FetchInstalledListAddonsIdleAction
    | FetchAvailableListAddonsRequestAction
    | FetchAvailableListAddonsSuccessAction
    | FetchAvailableListAddonsFailAction
    | FetchAvailableListAddonsIdleAction
    | RolloutPoliciesByMyOrganizationsRequestAction
    | RolloutPoliciesByMyOrganizationsSuccessAction
    | RolloutPoliciesByMyOrganizationsErrorAction
    | RolloutPoliciesBySpaceIdRequestAction
    | RolloutPoliciesBySpaceIdSuccessAction
    | RolloutPoliciesBySpaceIdErrorAction;
